import { endOfDay, subDays } from 'date-fns';
import type { IntlShape } from 'react-intl';

import { Field } from '~/components/Shared/Filters_DEPRECATED/types';
import { Filters } from '~/evifields/src/types/Filters';
import { Nullable } from '~/types/Nullable';

import { statusTypeOptions } from './constants';
import {
  Attempt,
  AttemptStatus,
  AttemptType,
  ConfigurationType,
  Endpoint,
  EndpointStatus,
  EndpointType,
  EventList,
  EventsType,
  FieldProps,
  SamplePayloadsType,
} from './types';

export const testIsActiveStatus = (status: EndpointStatus) =>
  status === 'active';

export const getStatusChip = (
  status: EndpointStatus | AttemptStatus,
  intl: IntlShape,
) => {
  switch (status) {
    case 'active':
      return {
        text: intl.formatMessage({
          id: 'WebhooksUtils.activeStatus',
          defaultMessage: 'Active',
          description: 'Active status label',
        }),
        status: 'success',
      };
    case 'suspended':
      return {
        text: intl.formatMessage({
          id: 'WebhooksUtils.suspendedStatus',
          defaultMessage: 'Suspended',
          description: 'Suspended status label',
        }),
        status: 'danger',
      };
    case 'failed':
      return {
        text: intl.formatMessage({
          id: 'WebhooksUtils.failedStatus',
          defaultMessage: 'Failed',
          description: 'Failed status label',
        }),
        status: 'danger',
      };
    case 'success':
      return {
        text: intl.formatMessage({
          id: 'WebhooksUtils.successStatus',
          defaultMessage: 'Success',
          description: 'Success status label',
        }),
        status: 'success',
      };
    default:
      return {
        text: intl.formatMessage({
          id: 'WebhooksUtils.unknownStatus',
          defaultMessage: 'Unknown',
          description: 'Unknown status label',
        }),
        status: 'info',
      };
  }
};

export const getDisabledTooltip = (
  configuration: Nullable<ConfigurationType>,
  intl: IntlShape,
) => {
  if (configuration) {
    if (!configuration?.url) {
      return intl.formatMessage({
        id: 'WebhooksUtils.provideValidUrlTooltip',
        defaultMessage: 'Please provide a valid URL',
        description: 'Tooltip for missing URL validation',
      });
    } else if (Boolean(checkValidUrl(configuration?.url, intl))) {
      return checkValidUrl(configuration?.url, intl);
    } else if (Object.keys(configuration?.events).length === 0) {
      return intl.formatMessage({
        id: 'WebhooksUtils.selectEventTooltip',
        defaultMessage: 'Select at least one event',
        description: 'Tooltip for missing event selection',
      });
    }
  }
};

export const getDisabledCopyTooltip = ({
  sampleEvent,
  samplePayloads,
  intl,
}: {
  sampleEvent?: Nullable<string>;
  samplePayloads?: SamplePayloadsType;
  intl: IntlShape;
}) => {
  return !sampleEvent
    ? intl.formatMessage({
        id: 'WebhooksUtils.selectEventToCopyTooltip',
        defaultMessage: 'Select an event to copy',
        description: 'Tooltip when no event is selected for copying',
      })
    : getEventSamplePayload(sampleEvent, samplePayloads) === null
      ? intl.formatMessage({
          id: 'WebhooksUtils.noSamplePayloadTooltip',
          defaultMessage:
            'This event type does not have a sample payload. Please try another event type.',
          description: 'Tooltip when event type has no sample payload',
        })
      : intl.formatMessage({
          id: 'WebhooksUtils.copyTooltip',
          defaultMessage: 'Copy',
          description: 'Copy action tooltip',
        });
};

export const getAttemptsDisabledTooltip = (
  isLoadingExportAttempts: boolean,
  intl: IntlShape,
) => {
  return isLoadingExportAttempts
    ? intl.formatMessage({
        id: 'WebhooksUtils.exportingAttemptsTooltip',
        defaultMessage: 'Exporting attempts...',
        description: 'Tooltip shown while exporting attempts',
      })
    : intl.formatMessage({
        id: 'WebhooksUtils.exportExcelTooltip',
        defaultMessage: 'Export Excel',
        description: 'Tooltip for export Excel action',
      });
};

export const transformToStringArray = (events: EventsType) => [
  ...Object.keys(events).reduce<string[]>((acc, key) => {
    return [...acc, ...events[key]];
  }, []),
];

export const checkActiveEndpointEvents = ({
  activeEndpoint,
  eventGroups,
}: {
  activeEndpoint: Endpoint;
  eventGroups: EventList[];
}) => {
  const updatedEventGroups = eventGroups.map((group) => ({
    ...group,
    options: group.options.map((option) => ({
      ...option,
      checked: activeEndpoint.events.includes(option.value) ?? false,
    })),
  }));

  const transformEvents = updatedEventGroups.reduce((acc, group) => {
    acc[group.name] = group.options
      .filter((option) => option.checked === true)
      .map((option) => option.value);
    return acc;
  }, {} as EventsType);

  return transformEvents;
};

export const getFields = ({
  eventGroups,
  endpoints,
  intl,
}: FieldProps): Record<string, Field> => {
  const today = new Date();
  const max = endOfDay(today);
  const min = subDays(today, 30);

  return {
    endpointUrl: {
      id: 'endpointUrl',
      label: intl.formatMessage({
        id: 'WebhooksUtils.endpointUrlLabel',
        defaultMessage: 'Endpoint URL',
        description: 'Label for endpoint URL field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        options: endpoints.map(({ endpointUrl, id }) => ({
          label: endpointUrl,
          value: id,
        })),
      },
    },
    eventType: {
      id: 'eventType',
      label: intl.formatMessage({
        id: 'WebhooksUtils.eventTypeLabel',
        defaultMessage: 'Event Type',
        description: 'Label for event type field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        options: eventGroups,
      },
    },
    dateDelivered: {
      id: 'dateDelivered',
      label: intl.formatMessage({
        id: 'WebhooksUtils.dateSentLabel',
        defaultMessage: 'Date Sent',
        description: 'Label for date sent field',
      }),
      type: 'date',
      whitelistOperatorIds: [
        'date_in_the_last',
        'date_on',
        'date_between',
        'date_after',
        'date_before',
      ],
      settings: {
        max,
        min,
        getDayTooltip: (date: Date) => {
          if (date > max) {
            return intl.formatMessage({
              id: 'WebhooksUtils.futureDateTooltip',
              defaultMessage: 'You can not select a future date',
              description: 'Tooltip for invalid future date selection',
            });
          } else if (date < min) {
            return intl.formatMessage({
              id: 'WebhooksUtils.pastDateTooltip',
              defaultMessage: 'You can not go back more than 30 days',
              description: 'Tooltip for date selection beyond 30 days',
            });
          }
        },
      },
    },
    status: {
      id: 'status',
      label: intl.formatMessage({
        id: 'WebhooksUtils.statusLabel',
        defaultMessage: 'Status',
        description: 'Label for status field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        options: statusTypeOptions,
      },
    },
  };
};
export const getEventSamplePayload = (
  event?: Nullable<string>,
  samplePayloads?: SamplePayloadsType,
) => {
  if (
    !event ||
    !samplePayloads ||
    !samplePayloads[event as keyof typeof samplePayloads]
  ) {
    return null;
  }

  const payload = samplePayloads[event as keyof typeof samplePayloads];
  return JSON.stringify(payload, null, 2);
};

export const getUpdatedFilters = ({
  filters,
  endpoint,
}: {
  filters: Filters;
  endpoint: Endpoint;
}) => {
  return filters.map((filter) => {
    if (filter.id === 'endpointUrl') {
      return {
        ...filter,
        values: [endpoint.id],
        label: endpoint.endpointUrl,
      };
    }
    if (filter.id === 'dateDelivered') {
      return {
        ...filter,
        values: [
          {
            unit: 'days',
            value: 29,
          },
        ],
      };
    }
    return filter;
  });
};
export const getStringifyValue = (value?: Record<string, any>) => {
  if (!value) {
    return '';
  }
  return JSON.stringify(value, null, 2);
};

export const checkValidUrl = (url: string, intl: IntlShape) => {
  if (url && !url.startsWith('https://')) {
    return intl.formatMessage({
      id: 'WebhooksUtils.invalidUrlHttpsMessage',
      defaultMessage: 'Invalid URL, please make sure it starts with https://',
      description: 'Error message for URL not starting with https',
    });
  } else if (url.startsWith('https://') && url.length < 9) {
    return intl.formatMessage({
      id: 'WebhooksUtils.invalidUrlCompleteMessage',
      defaultMessage:
        'Invalid URL. Please provide a complete url to make a valid request',
      description: 'Error message for incomplete URL',
    });
  } else {
    return undefined;
  }
};

export const getResponseStatus = (attempt: Attempt) => {
  let text;
  if (attempt.status === 'failed') {
    if (attempt.statusDescription) {
      text = attempt.statusDescription;
    } else {
      text = '-';
    }
  } else {
    if (attempt.responseStatus) {
      text = attempt.responseStatus;
    } else {
      text = '-';
    }
  }
  return text;
};

export const getAttemptTitle = (action: AttemptType, intl: IntlShape) => {
  switch (action) {
    case 'archive':
      return intl.formatMessage({
        id: 'AttemptModal.archiveTitle',
        defaultMessage: 'Archive Attempt',
        description: 'Modal title for archiving attempt',
      });
    case 'resend':
      return intl.formatMessage({
        id: 'AttemptModal.resendTitle',
        defaultMessage: 'Resend Attempt',
        description: 'Modal title for resending attempt',
      });
    case 'view':
    default:
      return intl.formatMessage({
        id: 'AttemptModal.viewTitle',
        defaultMessage: 'View Attempt',
        description: 'Modal title for viewing attempt',
      });
  }
};

export const getEndpointTitle = (action: EndpointType, intl: IntlShape) => {
  switch (action) {
    case 'add':
      return intl.formatMessage({
        id: 'EndpointModal.createTitle',
        defaultMessage: 'Add Endpoint',
        description: 'Modal title for creating endpoint',
      });
    case 'edit':
      return intl.formatMessage({
        id: 'EndpointModal.editTitle',
        defaultMessage: 'Edit Endpoint',
        description: 'Modal title for editing endpoint',
      });
    case 'delete':
      return intl.formatMessage({
        id: 'EndpointModal.deleteTitle',
        defaultMessage: 'Delete Endpoint',
        description: 'Modal title for deleting endpoint',
      });
    case 'suspend':
      return intl.formatMessage({
        id: 'EndpointModal.suspendTitle',
        defaultMessage: 'Suspend Endpoint',
        description: 'Modal title for suspending endpoint',
      });
    case 'activate':
      return intl.formatMessage({
        id: 'EndpointModal.activateTitle',
        defaultMessage: 'Activate Endpoint',
        description: 'Modal title for activating endpoint',
      });
    case 'rotate-key':
      return intl.formatMessage({
        id: 'EndpointModal.rotateKeyTitle',
        defaultMessage: 'Rotate Key',
        description: 'Modal title for rotating endpoint key',
      });
    case 'view':
      return intl.formatMessage({
        id: 'EndpointModal.viewTitle',
        defaultMessage: 'View Endpoint',
        description: 'Modal title for viewing endpoint',
      });
    default:
      return intl.formatMessage({
        id: 'EndpointModal.defaultTitle',
        defaultMessage: 'Endpoint',
        description: 'Default modal title for endpoint actions',
      });
  }
};
